import type { DriverStint } from './driving.ts'
import { driverTotals, stintDuration } from './driving.ts'
import type { Regime } from './racePlan.ts'

/**
 * How much of the race each driver has left.
 *
 * Two quite different kinds of limit, and a team is usually under one or the
 * other rather than both.
 *
 * A **time** limit caps cumulative driving. It is arithmetic on spans and needs
 * no attribution to be useful in aggregate, though it needs it to be useful per
 * person.
 *
 * A **count** limit — every driver takes the same number of stints, within one
 * — is not a deadline at all. It produces a different sentence: not "stop by
 * ten to three" but "he cannot take this one, he would be on four and three is
 * the most anybody may do". It is knowable before the race starts, and it is
 * the sort of thing forgotten at hour six while improvising around a safety car.
 *
 * The count limit is all or nothing. It counts stints PER DRIVER, so knowing
 * that a change happened is useless without knowing who — two stops inside one
 * window, one a driver change and one a penalty, are indistinguishable
 * otherwise. With partial attribution it reports nothing and says how many
 * stints are unaccounted for, because a figure computed from half the data
 * reads as "you are fine" right up until it is wrong.
 */

export interface DriverBudget {
  driverId: string
  name?: string
  /** Driving time so far, including pit time inside a stint. */
  usedMs: number
  /** Against a cumulative cap, where there is one. */
  remainingMs: number | null
  stints: number
  /** The most stints this driver may take, where a balance rule applies. */
  stintCeiling: number | null
  /** True while they are the one in the car. */
  current: boolean
  /** They have taken every stint they are allowed. */
  complete: boolean
  /** They are over a limit already. */
  over: boolean
}

export interface BudgetReport {
  drivers: DriverBudget[]
  /** Stints with nobody attributed. Non-zero means the counts cannot be trusted. */
  unattributed: number
  /** True when a count rule applies but attribution is incomplete. */
  incomplete: boolean
  /** How many stints the race is expected to take, where that is knowable. */
  expectedStints: number | null
}

/**
 * The most stints any one driver may take.
 *
 * Equal within one, so with twelve stints and four drivers everybody does
 * three, and with five drivers three of them do three and two do two. The
 * ceiling is the larger share, and it is derived rather than entered: it falls
 * out of the schedule and the size of the crew.
 */
export function stintCeiling(expectedStints: number, driverCount: number): number | null {
  if (expectedStints <= 0 || driverCount <= 0) return null
  return Math.ceil(expectedStints / driverCount)
}

/**
 * How many stints a race is expected to take.
 *
 * One more than the number of mandated stops, because a race begins with a
 * driver already in the car. Where stops are not mandated at all — a free-stop
 * race — this is unknowable in advance and returns null rather than a guess.
 */
export function expectedStints(regime: Regime): number | null {
  const windows = regime.windows?.entries.length ?? 0
  if (windows > 0) return windows + 1
  const min = regime.stops?.minCount
  return min && min > 0 ? min + 1 : null
}

export function driverBudgets(
  stints: readonly DriverStint[],
  regime: Regime | undefined,
  roster: readonly { id: string; name: string }[],
  now: Date,
): BudgetReport {
  const { totals, unattributed } = driverTotals(stints, now)
  const expected = regime ? expectedStints(regime) : null
  const balanced = regime?.driver?.stintBalance === 'equalWithinOne'

  // The ceiling counts the crew the team intends to use, not the number who
  // happen to have driven so far — otherwise it tightens every time somebody
  // new gets in, which is backwards.
  const crew = Math.max(roster.length, totals.length)
  const ceiling = balanced && expected ? stintCeiling(expected, crew) : null

  const cap = regime?.driver?.maxTotalDrivingMs ?? null

  const drivers: DriverBudget[] = totals.map((t) => ({
    driverId: t.driverId,
    name: roster.find((r) => r.id === t.driverId)?.name,
    usedMs: t.totalMs,
    remainingMs: cap == null ? null : cap - t.totalMs,
    stints: t.stints,
    stintCeiling: ceiling,
    current: t.current,
    complete: ceiling != null && t.stints >= ceiling,
    over: (cap != null && t.totalMs > cap) || (ceiling != null && t.stints > ceiling),
  }))

  // Anybody rostered who has not driven yet still belongs on the list: "Cowley
  // has done none" is exactly the thing a rotation is checked for.
  for (const r of roster) {
    if (drivers.some((d) => d.driverId === r.id)) continue
    drivers.push({
      driverId: r.id,
      name: r.name,
      usedMs: 0,
      remainingMs: cap,
      stints: 0,
      stintCeiling: ceiling,
      current: false,
      complete: false,
      over: false,
    })
  }

  return {
    drivers,
    unattributed,
    incomplete: !!ceiling && unattributed > 0,
    expectedStints: expected,
  }
}

/**
 * Who the rotation says takes the next stint.
 *
 * An order, not a schedule, so "next" is simply the one after whoever is in the
 * car. A driver not in the rotation — somebody who got in unexpectedly — hands
 * back to the top of it rather than to nobody, which is the more useful answer
 * when a plan has already been departed from.
 */
export function nextDriver(rotation: readonly string[], currentId: string | null): string | null {
  if (rotation.length === 0) return null
  if (!currentId) return rotation[0] ?? null
  const i = rotation.indexOf(currentId)
  if (i < 0) return rotation[0] ?? null
  return rotation[(i + 1) % rotation.length] ?? null
}

/**
 * Whether a driver may take another stint, and why not.
 *
 * Null when nothing forbids it. The count rule is only applied where every
 * stint has a driver, for the reason in the module comment.
 */
export function cannotDrive(
  budget: DriverBudget | undefined,
  report: BudgetReport,
  regime: Regime | undefined,
  stints: readonly DriverStint[],
): string | null {
  if (!budget) return null

  if (budget.remainingMs != null && budget.remainingMs <= 0) {
    return 'has used their full driving time'
  }
  if (!report.incomplete && budget.stintCeiling != null && budget.stints >= budget.stintCeiling) {
    return `has taken all ${budget.stintCeiling} of their stints`
  }
  const maxRun = regime?.driver?.maxConsecutiveStints
  if (maxRun && consecutive(stints, budget.driverId) >= maxRun) {
    return maxRun === 1 ? 'cannot take two stints in a row' : `cannot take more than ${maxRun} in a row`
  }
  return null
}

function consecutive(stints: readonly DriverStint[], driverId: string): number {
  let n = 0
  for (let i = stints.length - 1; i >= 0; i--) {
    if (stints[i]?.driverId !== driverId) break
    n++
  }
  return n
}

/** How long the driver in the car has been out, for a widget that shows it. */
export function currentStintMs(stints: readonly DriverStint[], now: Date): number | null {
  const last = stints.at(-1)
  return last ? stintDuration(last, now) : null
}
